/**
 * Utility functions for formatting text and data
 */

const ROLE_LABELS = {
  R1: "Owner",
  R3: "Professional",
  R4: "Freelancer",
  R7: "Booth Renter",
};

function humanizeTypeText(value) {
  if (!value) return "";

  return String(value)
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2")
    .replace(/[\-_.\/]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function toTitleCasePreservingAcronyms(value) {
  return value
    .split(" ")
    .filter(Boolean)
    .map((word) => {
      if (/^[A-Z0-9]{2,}$/.test(word)) {
        return word;
      }
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(" ");
}

/**
 * Format professional type for display (e.g., "hair-stylist" -> "Hair Stylist")
 * Handles both hyphenated and underscored formats
 * @param {string} type - The professional type (e.g., "hair-stylist", "nail_technician")
 * @returns {string} - Formatted professional type (e.g., "Hair Stylist", "Nail Technician")
 */
export function formatProfessionalType(type) {
  if (!type) return "";

  if (ROLE_LABELS[type]) {
    return ROLE_LABELS[type];
  }

  return toTitleCasePreservingAcronyms(humanizeTypeText(type));
}

/**
 * Format business type for display (e.g., "hair_salon" -> "Hair Salon")
 * @param {string} type - The business type
 * @returns {string} - Formatted business type
 */
export function formatBusinessType(type) {
  if (!type) return "";

  return toTitleCasePreservingAcronyms(humanizeTypeText(type));
}

/**
 * Get formatted professional type label from staff object
 * Checks multiple possible fields and returns a properly formatted string
 * @param {object} staff - Staff object with professional type information
 * @returns {string} - Formatted professional type label
 */
export function getProfessionalTypeLabel(staff) {
  if (!staff) return "Professional";
  
  // Check direct professionalType field
  if (staff.professionalType) {
    return formatProfessionalType(staff.professionalType);
  }
  
  // Check userId.professionalType
  if (staff.userId?.professionalType) {
    return formatProfessionalType(staff.userId.professionalType);
  }
  
  // Fallback to role if available
  if (staff.role) {
    return ROLE_LABELS[staff.role] || formatProfessionalType(staff.role);
  }
  
  return "Professional";
}

/**
 * Capitalize first letter of a string
 * @param {string} str - Input string
 * @returns {string} - String with first letter capitalized
 */
export function capitalizeFirst(str) {
  return str ? str.charAt(0).toUpperCase() + str.slice(1) : "";
}
